import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import { javascript } from '@codemirror/lang-javascript'
import {
  bracketMatching,
  defaultHighlightStyle,
  foldGutter,
  syntaxHighlighting,
} from '@codemirror/language'
import { Annotation, EditorState } from '@codemirror/state'
import {
  EditorView,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
  placeholder as editorPlaceholder,
} from '@codemirror/view'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { uiColors } from '~/common/styles/colors'
import { dossMonoFont } from '~/common/styles/theme'

const embedSourceEditorTheme = EditorView.theme({
  '&': {
    height: '100%',
    backgroundColor: uiColors.background.white,
    color: uiColors.content.primary,
    fontSize: '12px',
  },
  '&.cm-focused': {
    outline: `1px solid ${uiColors.border.focus}`,
  },
  '.cm-scroller': {
    fontFamily: dossMonoFont,
    overflow: 'auto',
  },
  '.cm-content': {
    minHeight: '100%',
    padding: '12px 0',
  },
  '.cm-gutters': {
    backgroundColor: uiColors.background.offWhite,
    borderRight: `1px solid ${uiColors.border.normal}`,
    color: uiColors.content.disabled,
  },
})

const externalValueUpdateAnnotation = Annotation.define<boolean>()

export const EmbedSourceEditor = ({
  ariaLabel,
  onChange,
  placeholder,
  value,
}: {
  ariaLabel: string
  onChange: (value: string) => void
  placeholder: string
  value: string
}): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const viewRef = useRef<EditorView | null>(null)
  const initialValueRef = useRef(value)

  // Create one editor view for this editing session and report only user-originated changes.
  useEffect(() => {
    if (containerRef.current === null) {
      return
    }
    const state = EditorState.create({
      doc: initialValueRef.current,
      extensions: [
        javascript({ jsx: true, typescript: true }),
        syntaxHighlighting(defaultHighlightStyle),
        embedSourceEditorTheme,
        lineNumbers(),
        highlightActiveLine(),
        highlightActiveLineGutter(),
        foldGutter(),
        history(),
        bracketMatching(),
        closeBrackets(),
        editorPlaceholder(placeholder),
        EditorState.tabSize.of(2),
        EditorView.contentAttributes.of({ 'aria-label': ariaLabel }),
        EditorView.updateListener.of((update) => {
          const isExternal = update.transactions.some(
            (transaction) => transaction.annotation(externalValueUpdateAnnotation) === true,
          )
          if (update.docChanged && !isExternal) {
            onChange(update.state.doc.toString())
          }
        }),
        keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...historyKeymap]),
      ],
    })
    const view = new EditorView({ state, parent: containerRef.current })
    viewRef.current = view
    return () => {
      view.destroy()
      viewRef.current = null
    }
  }, [ariaLabel, onChange, placeholder])

  // Keep the editor controlled when the draft changes outside CodeMirror.
  useLayoutEffect(() => {
    const view = viewRef.current
    if (view === null || view.state.doc.toString() === value) {
      return
    }
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: value },
      annotations: externalValueUpdateAnnotation.of(true),
    })
  }, [value])

  return <div ref={containerRef} className="size-full" />
}
